import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useGetProductBrandByIdQuery,
  useUpdateProductBrandMutation,
} from '@app/api/Products';
import type { ModelProductBrand } from '@app/api/Products';
import { BRANDS_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import type {
  AddEditBrandForm,
  AddEditBrandProps,
} from '@app/pages/Warehouses/Brands/components/AddEditBrand/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

const { useForm } = Form;

export const useAddEditBrand = ({ mode }: AddEditBrandProps) => {
  const [searchParams] = useSearchParams();
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const [updateBrand, { isLoading: isUpdateBrandLoading }] =
    useUpdateProductBrandMutation();
  const navigate = useNavigate();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [form] = useForm<AddEditBrandForm>();
  const { resetFields, setFieldsValue } = form;
  const {
    userAccessRights: {
      Products: { IsBrandDelete, IsBrandCreate, IsBrandEdit },
    },
  } = useNewUserAccessRights();

  const brandId = useMemo(() => {
    if (mode !== 'edit') {
      return null;
    }

    const id = searchParams.get('id');

    if (id) {
      const parsedId = parseInt(id, 10);
      if (isNaN(parsedId)) {
        showNotification(
          'Некорректный идентификатор бренда. Пожалуйста, проверьте URL.',
          'frontWarning'
        );
        return null;
      }
      return parsedId;
    }
    return null;
  }, [searchParams, mode, showNotification]);

  const isPageForbidden = useMemo(() => {
    switch (mode) {
      case 'add':
        return !IsBrandCreate;
      case 'edit':
        return !IsBrandEdit;
      default:
        return false;
    }
  }, [mode, IsBrandCreate, IsBrandEdit]);

  const { data, isFetching, isLoading, isUninitialized } =
    useGetProductBrandByIdQuery(
      {
        ActiveBranchId,
        brandId: brandId || 0,
      },
      { skip: !brandId || mode !== 'edit' }
    );

  const disabledForm = useMemo(() => {
    switch (mode) {
      case 'add':
        return isUpdateBrandLoading;
      case 'edit':
        return (
          isFetching || isLoading || isUninitialized || isUpdateBrandLoading
        );
      default:
        return false;
    }
  }, [isLoading, isFetching, isUninitialized, isUpdateBrandLoading, mode]);

  const skipForm = useCallback(() => {
    resetFields();
  }, [resetFields]);

  const initForm = useCallback(() => {
    if (mode === 'add') {
      skipForm();
      return;
    }
    if (data) {
      const { Name, Comment } = data;
      setFieldsValue({ Name: Name, Comment: Comment || '' });
    }
  }, [skipForm, setFieldsValue, mode, data]);

  const goToBrands = useCallback(() => {
    navigate(BRANDS_PATH);
  }, [navigate]);

  const openDeleteModal = useCallback(() => {
    setIsDeleteOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteOpen(false);
  }, []);

  const onCancel = useCallback(() => {
    skipForm();
    closeDeleteModal();
    goToBrands();
  }, [skipForm, closeDeleteModal, goToBrands]);

  const updateBrandHandler = useCallback(
    async (values: ModelProductBrand) => {
      try {
        const response = await updateBrand({
          ActiveBranchId,
          data: values,
        }).unwrap();

        onCancel();

        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [updateBrand, onCancel, ActiveBranchId]
  );

  const onFinish = useCallback(
    async ({ Name, Comment }: AddEditBrandForm) => {
      try {
        await updateBrandHandler({
          Id: brandId || 0,
          Name,
          Comment: Comment || '',
          IsDeleted: false,
          CountProducts: 0,
        });

        showNotification(
          `Бренд "${Name}" успешно ${mode === 'add' ? 'создан' : 'обновлен'}.`,
          'success'
        );
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [showNotification, brandId, mode, updateBrandHandler]
  );

  const onDelete = useCallback(async () => {
    if (!data) {
      showNotification('Бренд не найден.', 'frontWarning');
      return;
    }

    try {
      const response = await updateBrandHandler({
        ...data,
        IsDeleted: true,
      });

      const { Name } = response;

      showNotification(`Бренд "${Name}" успешно удален.`, 'success');
      setIsDeleteOpen(false);
      skipForm();
      goToBrands();
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [data, updateBrandHandler, showNotification, skipForm, goToBrands]);

  return {
    form,
    initForm,
    onFinish,
    openDeleteModal,
    closeDeleteModal,
    onDelete,
    isDeleteOpen,
    onCancel,
    disabledForm,
    skipForm,
    IsBrandDelete,
    isPageForbidden,
  };
};
